import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaInfoCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveModal } from '../../../actions/modalActions';
import { CONFIRMATION, RECIPIENT } from '../../../constants/documentTypes/modals/modals';
import { getCountry } from '../../../helpers/apiHelpers/registerShipmentHelpers';
import { getCurrentLangPretty } from '../../../helpers/url/urlHelpers';
import myPostWrapper from '../../../hoc/myPostWrapper';
import useCountries from '../../../hooks/useCountries';
import useRecipients from '../../../hooks/useRecipients';
import { ReactComponent as IconBin } from '../../../icons/bin.svg';
import { ReactComponent as IconPerson } from '../../../icons/contacts.svg';
import { ReactComponent as IconPencil } from '../../../icons/pencil.svg';
import IUserProfileReducer from '../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../reducers';
import { Column } from '../../base/grid';
import { A, H1 } from '../../base/typography';
import { RawLoader } from '../../loading/loading';
import Pagination from '../../search/searchLandingPage/searchResults/pagination';
import * as StyledRecipients from './recipients.styles';

interface Props {}

const Recipients: FC<Props> = () => {
	const dispatch = useDispatch<any>();
	const { t } = useTranslation('myShipments');
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile } = userProfileReducer;
	const { countries } = useCountries(getCurrentLangPretty(), true);

	const pageSize = 10;
	const [page, setPage] = useState(1);

	const { recipients, recipientsError, recipientsLoading } = useRecipients(userProfile);

	return (
		<>
			<div>
				<H1 marginBottom="3rem">{t('navigation.recipients')}</H1>
				<StyledRecipients.InfoField>
					<FaInfoCircle />
					<span>{t('page-recipients.info') as React.ReactNode}</span>
				</StyledRecipients.InfoField>
				<StyledRecipients.Wrapper>
					<table>
						<thead>
							<tr>
								<th>{t('name') as string}</th>
								<th>{t('address') as string}</th>
								<th colSpan={2}>{t('country') as string}</th>
							</tr>
						</thead>
						<tbody>
							{(recipientsLoading || !countries) && (
								<tr>
									<td colSpan={4}>
										<div className="center">
											<RawLoader small />
										</div>
									</td>
								</tr>
							)}
							{!recipientsLoading &&
								(!recipients || recipientsError || (recipients && recipients.length === 0)) && (
									<tr>
										<td colSpan={4}>
											<div className="center">
												{t('page-recipients.noRecipients') as React.ReactNode}
											</div>
										</td>
									</tr>
								)}
							{countries &&
								!recipientsLoading &&
								!recipientsError &&
								recipients &&
								recipients.map((recipient, index) => {
									if ((page - 1) * pageSize <= index && index < page * pageSize)
										return (
											<tr key={index}>
												<td>{recipient.name}</td>
												<td>
													{recipient.address?.street1 +
														' - ' +
														recipient.address?.postalCode +
														' ' +
														recipient.address?.city}
												</td>
												<td>{getCountry(recipient.address?.country, countries).label}</td>
												<td>
													<A
														onClick={() => {
															dispatch(
																setActiveModal(
																	RECIPIENT,
																	t('page-recipients.editRecipient') +
																		': ' +
																		recipient.name,
																	null,
																	recipient.id
																)
															);
														}}>
														<IconPencil />
													</A>
													<A
														onClick={() => {
															dispatch(
																setActiveModal(
																	CONFIRMATION,
																	t('remove') + ' ' + recipient.name + ' ?',
																	'DELETE_RECIPIENT',
																	recipient.id
																)
															);
														}}>
														<IconBin />
													</A>
												</td>
											</tr>
										);
									return null;
								})}
						</tbody>
					</table>
				</StyledRecipients.Wrapper>
				<StyledRecipients.StyledColumns>
					<Column tablet={1 / 2}>
						<A onClick={() => dispatch(setActiveModal(RECIPIENT, t('page-recipients.addRecipient')))}>
							<IconPerson />
							<span>{t('page-recipients.addRecipient') as React.ReactNode}</span>
						</A>
					</Column>
					{recipients && recipients.length > pageSize && (
						<Column tablet={1 / 2}>
							<Pagination
								numberOfGroups={Math.ceil(recipients.length / pageSize)}
								onChange={val => setPage(val + 1)}
								marginPagesDisplayed={1}
								pageRangeDisplayed={1}
							/>
						</Column>
					)}
				</StyledRecipients.StyledColumns>
			</div>
		</>
	);
};

export default myPostWrapper(Recipients);
